function _slicedToArray(r, e) { return _arrayWithHoles(r) || _iterableToArrayLimit(r, e) || _unsupportedIterableToArray(r, e) || _nonIterableRest(); }
function _nonIterableRest() { throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }
function _unsupportedIterableToArray(r, a) { if (r) { if ("string" == typeof r) return _arrayLikeToArray(r, a); var t = {}.toString.call(r).slice(8, -1); return "Object" === t && r.constructor && (t = r.constructor.name), "Map" === t || "Set" === t ? Array.from(r) : "Arguments" === t || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(t) ? _arrayLikeToArray(r, a) : void 0; } }
function _arrayLikeToArray(r, a) { (null == a || a > r.length) && (a = r.length); for (var e = 0, n = Array(a); e < a; e++) n[e] = r[e]; return n; }
function _iterableToArrayLimit(r, l) { var t = null == r ? null : "undefined" != typeof Symbol && r[Symbol.iterator] || r["@@iterator"]; if (null != t) { var e, n, i, u, a = [], f = !0, o = !1; try { if (i = (t = t.call(r)).next, 0 === l) { if (Object(t) !== t) return; f = !1; } else for (; !(f = (e = i.call(t)).done) && (a.push(e.value), a.length !== l); f = !0); } catch (r) { o = !0, n = r; } finally { try { if (!f && null != t.return && (u = t.return(), Object(u) !== u)) return; } finally { if (o) throw n; } } return a; } }
function _arrayWithHoles(r) { if (Array.isArray(r)) return r; }
import { useContext, useDebugValue, useEffect, useMemo, useState } from 'react';
import { nanoid } from 'nanoid';
import { PersistentStateContext } from "./PersistentStateContext.js";
import { useDhId } from "./useDhId.js";
/**
 * Migrates persisted state to the provided version using the provided migrations.
 *
 * @param state The current state
 * @param from The current version
 * @param to The version to migrate to
 * @param migrations The list of all migrations (may include those already applied)
 * @param type The type of the state. Used for better error messages
 * @returns The state at the new version
 * @throws Error if trying to migrate backwards or no migration exists for the to version
 */
function migrateState(state, from, to, migrations, type) {
  if (from > to) {
    throw new Error("Persisted state ".concat(type, " is a newer version than the current version. Persisted version: ").concat(from, ". Current version: ").concat(to));
  }
  var migratedState = state;
  var currentVersion = from;
  while (currentVersion < to) {
    // eslint-disable-next-line no-loop-func
    var possibleMigration = migrations.filter(m => m.from === currentVersion);
    if (possibleMigration.length === 0) {
      throw new Error("No migration found for persisted state ".concat(type, " from version ").concat(currentVersion));
    }
    if (possibleMigration.length > 1) {
      throw new Error("Multiple migrations found for persisted state ".concat(type, " from version ").concat(currentVersion));
    }
    var migration = possibleMigration[0];
    try {
      migratedState = migration.migrate(migratedState);
      currentVersion += 1;
    } catch (e) {
      throw new Error("Error migrating persisted state ".concat(type, " from version ").concat(migration.from, ": ").concat(e), {
        cause: e
      });
    }
  }
  return migratedState;
}

/**
 * Functions identically to useState except that a PersistentStateProvider can be used to
 * track all calls to this hook and persist the value for future page loads.
 * Primarily used in Deephaven UI so we can persist state of multiple components within a panel.
 *
 * @param initialState The initial state if there is no previously persisted state.
 * @param config.type The type of the state. This identifier is used to validate the state being rehydrated. Should be unique to your component.
 * @param config.version The version of the state. This should be an integer, and is used to determine if the state needs to be migrated. Value should be incremented when data structure changes for this type.
 * @param config.migrations An array of migrations to apply to the state if the version of the persisted state is below the current version. Each migration increments the version by 1.
 * @param config.migrations.from The starting version of the migration. The migration will increment the version by 1.
 * @param config.migrations.migrate The function to call to migrate the state.
 * @param config.deleteOnUnmount If true, the state will be deleted from the PersistentStateProvider when the component using this hook is unmounted. Defaults to true.
 *                               May be useful to set to false for components which are conditionally rendered within a panel like console creator settings.
 * @returns [state, setState] tuple just like useState.
 */
export function usePersistentState(initialState, config) {
  var panelId = useDhId();
  var hookId = useMemo(() => nanoid(), []);
  var context = useContext(PersistentStateContext);
  var type = config.type,
    version = config.version,
    _config$migrations = config.migrations,
    migrations = _config$migrations === void 0 ? [] : _config$migrations,
    _config$deleteOnUnmou = config.deleteOnUnmount,
    deleteOnUnmount = _config$deleteOnUnmou === void 0 ? true : _config$deleteOnUnmou;
  var _useState = useState(() => {
      var persistedData = panelId != null ? context === null || context === void 0 ? void 0 : context.getState(panelId, type) : undefined;
      if (persistedData == null) {
        return typeof initialState === 'function' ? initialState() : initialState;
      }
      if (persistedData.version !== version) {
        return migrateState(persistedData.state, persistedData.version, version, migrations, type);
      }
      return persistedData.state;
    }),
    _useState2 = _slicedToArray(_useState, 2),
    state = _useState2[0],
    setState = _useState2[1];
  var stateWithConfig = useMemo(() => ({
    type,
    version,
    state
  }), [state, type, version]);
  useDebugValue(stateWithConfig);
  useEffect(function addState() {
    if (panelId != null) {
      context === null || context === void 0 || context.addState(hookId, panelId, stateWithConfig);
    }
  }, [context, hookId, panelId, stateWithConfig]);
  useEffect(function removeOnUnmount() {
    return () => {
      if (deleteOnUnmount && panelId != null) {
        context === null || context === void 0 || context.removeState(hookId, panelId, type);
      }
    };
  }, [context, deleteOnUnmount, hookId, panelId, type]);
  useEffect(() => () => {
    if (panelId != null) {
      context === null || context === void 0 || context.deregisterHook(hookId, panelId, type);
    }
  }, [context, hookId, panelId, type]);
  return [state, setState];
}
export default usePersistentState;
//# sourceMappingURL=usePersistentState.js.map